// src/components/AddVendor.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close'; // Import Close icon
import axios from 'axios';
import env from "react-dotenv";

const AddVendor = ({ open, handleClose, fetchVendors }) => {
  const navigate = useNavigate();

  const [vendorCode, setVendorCode] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [gstNo, setGstNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`)
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
      });
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!vendorCode.trim()) newErrors.vendorCode = 'Please enter a vendor code';
    if (!vendorName.trim()) newErrors.vendorName = 'Please enter a vendor name';
    if (!address.trim()) newErrors.address = 'Please enter an address';
    if (!phone.trim()) newErrors.phone = 'Please enter a phone';
    if (!city.trim()) newErrors.city = 'Please enter city';
    if (!state.trim()) newErrors.state = 'Please enter state';
    if (!pinCode.trim() || isNaN(pinCode)) newErrors.pinCode = 'Please enter a pin code';
    if (!gstNo.trim()) newErrors.gstNo = 'Please enter a GST No.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'vendorCode':
        setVendorCode(value);
        break;
      case 'vendorName':
        setVendorName(value);
        break;
      case 'address':
        setAddress(value);
        break;
      case 'phone':
        setPhone(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'pinCode':
        setPinCode(value);
        break;
      case 'gstNo':
        setGstNo(value);
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setLoading(true);

    // Check if the vendorCode already exists
    axios
      .get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/vendors/exists`, {
        params: { vendor_code: vendorCode },
      })
      .then((response) => {
        if (response.data.exists) {
          setErrorMessage('Vendor Code already exists. Please use a unique Vendor Code.');
          setLoading(false);
        } else {
          // If vendorCode doesn't exist, create the vendor
          const newVendor = {
            vendor_code: vendorCode,
            vendor_name: vendorName,
            address,
            phone,
            city,
            state,
            pin_code: pinCode,
            gst_no: gstNo,
            product_list: selectedProducts.join(', '), // Save as comma-separated values
          };

          axios
            .post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/vendors`, newVendor)
            .then(() => {
              fetchVendors();
              handleCloseModal();
              navigate('/vendorHome');
              setLoading(false);
            })
            .catch((error) => {
              console.error('Error adding vendor:', error.response?.data || error);
              setErrorMessage('An error occurred while adding the vendor.');
              setLoading(false);
            });
        }
      })
      .catch((error) => {
        console.error('Error checking vendor code:', error);
        setErrorMessage('Failed to validate Vendor Code. Please try again.');
        setLoading(false);
      });
  };

  const handleCloseSnackbar = () => {
    setErrorMessage('');
  };

  // Function to reset form values
  const resetForm = () => {
    setVendorCode('');
    setVendorName('');
    setAddress('');
    setPhone('');
    setCity('');
    setState('');
    setPinCode('');
    setGstNo('');
    setSelectedProducts([]);
    setErrors({});
  };
  // Close modal and reset form without refreshing
  const handleCloseModal = () => {
    resetForm();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
          Add Vendor
        </Typography>
         <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor Code"
              name="vendorCode"
              variant="outlined"
              fullWidth
              value={vendorCode}
              onChange={handleChange}
              error={!!errors.vendorCode}
              helperText={errors.vendorCode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor Name"
              name="vendorName"
              variant="outlined"
              fullWidth
              value={vendorName}
              onChange={handleChange}
              error={!!errors.vendorName}
              helperText={errors.vendorName}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={products.map((product) => product.product_name)} // Map product names
              value={selectedProducts}
              onChange={(event, newValue) => setSelectedProducts(newValue)}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    key={option}
                    label={option}
                    {...getTagProps({ index })}
                    sx={{
                      backgroundColor: '#e0f7fa', // Light teal background
                      color: '#00796b',          // Dark teal text
                      '& .MuiChip-deleteIcon': {
                        color: '#004d40',        // Dark teal delete icon
                      },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Raw Material"
                  placeholder="Select Raw Material"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              name="address"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              name="phone"
              variant="outlined"
              fullWidth
              type="tel"
              value={phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              name="city"
              variant="outlined"
              fullWidth
              value={city}
              onChange={handleChange}
              error={!!errors.city}
              helperText={errors.city}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="State"
              name="state"
              variant="outlined"
              fullWidth
              value={state}
              onChange={handleChange}
              error={!!errors.state}
              helperText={errors.state}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Pin Code"
              name="pinCode"
              variant="outlined"
              fullWidth
              type="number"
              value={pinCode}
              onChange={handleChange}
              error={!!errors.pinCode}
              helperText={errors.pinCode}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="GST No"
              name="gstNo"
              variant="outlined"
              fullWidth
              value={gstNo}
              onChange={handleChange}
              error={!!errors.gstNo}
              helperText={errors.gstNo}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button onClick={handleCloseModal} color="secondary" disabled={loading}
        sx={{ marginRight: 2,':hover': {
                      bgcolor: '#4863C0', // theme.palette.primary.main
                      color: 'white',
            }, }}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}
        sx={{backgroundColor:'#4863A0',':hover': {
                      bgcolor: '#4863C0', // theme.palette.primary.main
                      color: 'white',
                    },}} 
        variant="contained"
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

AddVendor.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchVendors: PropTypes.func.isRequired,
};

export default AddVendor;
